import { AlertCircle, Briefcase, CalendarDays, Euro } from "lucide-react";
import { useTranslate } from "ra-core";
import type { CSSProperties } from "react";

import { cn } from "@/lib/utils";

import type {
  AttentionGroupKey,
  CollectSummary,
  HomeCounters,
} from "./homeModel";
import { jumpToGroup, useTodayBreakdownLabel } from "./homeUi";
import { useHomeFormat } from "./useHomeFormat";

interface CounterProps {
  group: AttentionGroupKey;
  icon: typeof AlertCircle;
  tone: "danger" | "info" | "warning" | "neutral";
  label: string;
  value: string;
  unit?: string;
  hint: string;
  emphasis?: boolean;
  compact?: boolean;
}

const Counter = ({
  group,
  icon: Icon,
  tone,
  label,
  value,
  unit,
  hint,
  emphasis,
  compact,
}: CounterProps) => (
  <button
    type="button"
    className={cn("home-counter desk-press", compact && "home-counter--chip")}
    data-tone={tone}
    data-emphasis={emphasis || undefined}
    // The compact chip's value shrinks with its length (home.css).
    style={compact ? ({ "--chars": value.length } as CSSProperties) : undefined}
    onClick={() => jumpToGroup(group)}
  >
    <span className="home-counter__icon" aria-hidden>
      <Icon className="size-[18px]" strokeWidth={1.75} />
    </span>
    <span className="home-counter__body">
      <span className="home-counter__label">{label}</span>
      <span className="home-counter__value">
        {value}
        {unit ? <span className="home-counter__unit"> {unit}</span> : null}
      </span>
      {compact ? null : <span className="home-counter__hint">{hint}</span>}
    </span>
  </button>
);

/**
 * The four actionable counters of view A: Vencido, Hoy, Sin próximo paso and
 * Por cobrar. Each one jumps to its group in «Necesita tu atención».
 * `compact` renders the mobile chips (label + number).
 */
export const TodayCounters = ({
  counters,
  collect,
  compact = false,
}: {
  counters: HomeCounters;
  collect: CollectSummary;
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const format = useHomeFormat();
  const breakdown = useTodayBreakdownLabel();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.counters.${key}`, options);

  return (
    <div
      className={compact ? "home-m__kpis" : "home-counters"}
      role="group"
      aria-label={translate("desk.home.counters.label")}
    >
      <Counter
        group="overdue"
        icon={AlertCircle}
        tone="danger"
        emphasis={counters.overdue > 0}
        compact={compact}
        label={t("overdue")}
        value={String(counters.overdue)}
        unit={
          compact ? undefined : t("items", { smart_count: counters.overdue })
        }
        hint={
          counters.overdue > 0
            ? t("oldest", { smart_count: counters.oldestOverdueDays })
            : t("overdue_none")
        }
      />
      <Counter
        group="today"
        icon={CalendarDays}
        tone="info"
        compact={compact}
        label={t("today")}
        value={String(counters.today)}
        unit={compact ? undefined : t("items", { smart_count: counters.today })}
        hint={breakdown(counters.todayBreakdown) || t("today_none")}
      />
      <Counter
        group="no_next_step"
        icon={Briefcase}
        tone="warning"
        compact={compact}
        label={t(compact ? "no_next_step_short" : "no_next_step")}
        value={String(counters.noNextStep)}
        unit={
          compact ? undefined : t("deals", { smart_count: counters.noNextStep })
        }
        hint={
          counters.noNextStep > 0
            ? t("no_next_step_hint", {
                amount: format.money(counters.noNextStepAmount),
              })
            : t("no_next_step_none")
        }
      />
      <Counter
        group="collect"
        icon={Euro}
        tone="neutral"
        compact={compact}
        label={t("collect")}
        value={format.money(collect.total)}
        hint={
          collect.overdue > 0
            ? t("collect_overdue", { amount: format.money(collect.overdue) })
            : t("collect_none")
        }
      />
    </div>
  );
};
